/**
 * Flightline — Core Domain Models
 * --------------------------------------------------------------------------
 * Location : /src/domain/models/index.ts
 * Runs in  : Simulation Web Worker AND UI thread (type-only imports on the UI
 *            side). This module contains no UI state, no rendering concerns
 *            and no side effects.
 *
 * Source of truth:
 *   - airports.md          (OpenFlights airports.dat column layout)
 *   - aircraft.md          (aircraft name / IATA / ICAO catalogue)
 *   - api_contracts.md     (FlightLeg, CabinConfiguration, RouteAnalytics,
 *                           FinancialLedger are referenced by UICommand /
 *                           WorkerEvent)
 *   - Project Instructions (SPEC 1: schemas, cabin unit budget, maintenance
 *                           cycles, HQ departments)
 *
 * Conventions
 *   - Money is USD, stored as a plain number (`UsdAmount`).
 *   - Distances are kilometres, weights kilograms, runway/elevation metres,
 *     seat pitch inches (industry convention).
 *   - Simulation time is `SimMinutes`: whole minutes since the simulation
 *     epoch (00:00 UTC on day 0). All schedule times are UTC.
 *   - Percentages named `...Pct` are 0–100. Ratios named `...Ratio`,
 *     `...Multiplier` or `...Share` are plain fractions / factors.
 *   - Everything is `readonly` at the interface level; the simulation core
 *     produces new state objects rather than mutating shared ones.
 */

/* ========================================================================== *
 * 1. PRIMITIVES
 * ========================================================================== */

/** IATA airport code, e.g. "JFK". Primary key for AirportNode. */
export type AirportId = string;
/** ICAO airport code, e.g. "KJFK". */
export type IcaoCode = string;
/** ISO 3166-1 alpha-2 country code, e.g. "US". */
export type CountryCode = string;
/** Aircraft type key, e.g. "A320neo". */
export type AircraftTypeId = string;
/** Airframe family key used for fleet commonality, e.g. "A320". */
export type AircraftFamilyId = string;
/** Engine family key used for fleet commonality, e.g. "CFM-LEAP". */
export type EngineFamilyId = string;
/** Tail registration, e.g. "G-FLNA". Primary key for AircraftInstance. */
export type Registration = string;
/** FlightLeg primary key. Plain string to stay compatible with api_contracts. */
export type LegId = string;
export type ScheduleId = string;
export type FlightOperationId = string;
export type HedgeContractId = string;
export type CampaignId = string;
/** "ORG-DST" directional route key, e.g. "JFK-LHR". */
export type RouteKey = string;

export type UsdAmount = number;
/** Whole minutes since the simulation epoch (00:00 UTC, day 0). */
export type SimMinutes = number;
/** Whole days since the simulation epoch. */
export type SimDay = number;

/** 0 = Monday … 6 = Sunday. */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type MonthOfYear = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type HourOfDay =
  | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
  | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23;

type FixedLengthArray<
  T,
  N extends number,
  Acc extends readonly T[] = []
> = Acc['length'] extends N ? Acc : FixedLengthArray<T, N, [...Acc, T]>;

/** Exactly 24 values, index = hour of day (UTC unless stated otherwise). */
export type HourlyProfile = Readonly<FixedLengthArray<number, 24>>;

/** Calendar view of a simulation instant; inputs to seasonality/day-of-week. */
export interface SimClock {
  readonly nowMinutes: SimMinutes;
  readonly day: SimDay;
  readonly year: number;
  readonly month: MonthOfYear;
  readonly dayOfMonth: number;
  readonly dayOfWeek: DayOfWeek;
  readonly hour: HourOfDay;
  readonly minuteOfDay: number;
}

/** A directional origin–destination market. */
export interface ODPair {
  readonly originId: AirportId;
  readonly destinationId: AirportId;
}

/* ========================================================================== *
 * 2. SEAT CLASSES
 * ========================================================================== */

export type SeatClass = 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST';

export const SEAT_CLASSES: readonly SeatClass[] = [
  'ECONOMY',
  'PREMIUM_ECONOMY',
  'BUSINESS',
  'FIRST',
];

/** One value per seat class. */
export type ClassMap<T> = Readonly<Record<SeatClass, T>>;

export interface SeatClassSpec {
  readonly seatClass: SeatClass;
  /** Cabin floor units consumed by one seat of this class. */
  readonly unitsPerSeat: number;
  readonly minPitchInches: number;
  readonly maxPitchInches: number;
  readonly defaultPitchInches: number;
}

/**
 * Seat class constants from SPEC 1 §2.
 * Economy 1.0 u (28"–34"), Premium Economy 1.5 u (38"),
 * Business lie-flat 3.5 u (60"–78"), First suite 6.0 u.
 * The spec gives no first-class pitch; 78"–90" is the design default.
 */
export const SEAT_CLASS_SPECS: ClassMap<SeatClassSpec> = {
  ECONOMY: {
    seatClass: 'ECONOMY',
    unitsPerSeat: 1.0,
    minPitchInches: 28,
    maxPitchInches: 34,
    defaultPitchInches: 31,
  },
  PREMIUM_ECONOMY: {
    seatClass: 'PREMIUM_ECONOMY',
    unitsPerSeat: 1.5,
    minPitchInches: 38,
    maxPitchInches: 38,
    defaultPitchInches: 38,
  },
  BUSINESS: {
    seatClass: 'BUSINESS',
    unitsPerSeat: 3.5,
    minPitchInches: 60,
    maxPitchInches: 78,
    defaultPitchInches: 72,
  },
  FIRST: {
    seatClass: 'FIRST',
    unitsPerSeat: 6.0,
    minPitchInches: 78,
    maxPitchInches: 90,
    defaultPitchInches: 80,
  },
};

/* ========================================================================== *
 * 3. AIRPORTS
 * ========================================================================== */

/** OpenFlights `type` column. */
export type AirportKind = 'airport' | 'station' | 'port' | 'unknown';

/** OpenFlights `source` column. */
export type AirportDataSource = 'OurAirports' | 'Legacy' | 'User';

/**
 * OpenFlights DST rule column:
 * E Europe, A US/Canada, S South America, O Australia, Z New Zealand,
 * N None, U Unknown.
 */
export type DstRule = 'E' | 'A' | 'S' | 'O' | 'Z' | 'N' | 'U';

/**
 * One parsed row of airports.md, exactly in file column order:
 * id, name, city, country, IATA, ICAO, latitude, longitude, altitude (ft),
 * timezone offset, DST, tz database name, type, source.
 * Nullable fields correspond to the `\N` marker in the CSV.
 */
export interface AirportSourceRecord {
  readonly openFlightsId: number;
  readonly name: string;
  readonly city: string;
  readonly country: string;
  readonly iata: string | null;
  readonly icao: string | null;
  readonly latitude: number;
  readonly longitude: number;
  readonly altitudeFeet: number;
  readonly utcOffsetHours: number | null;
  readonly dstRule: DstRule;
  readonly tzDatabaseName: string | null;
  readonly kind: AirportKind;
  readonly source: AirportDataSource;
}

/**
 * Game-ready airport. Built by the data loader from an AirportSourceRecord
 * plus the economic/runway enrichment tables.
 */
export interface AirportNode {
  /** IATA code, e.g. "JFK". */
  readonly id: AirportId;
  readonly icao: IcaoCode | null;
  readonly openFlightsId: number;
  readonly name: string;
  readonly city: string;
  /** Country name as published in airports.md. */
  readonly country: string;
  /** ISO alpha-2 code used by reputation / marketing maps. */
  readonly countryCode: CountryCode;
  readonly lat: number;
  readonly lng: number;
  /** Converted from airports.md altitude (feet) to metres by the loader. */
  readonly elevationMeters: number;
  /** IANA tz database name, e.g. "America/New_York". */
  readonly timezone: string;
  readonly utcOffsetHours: number;
  readonly dstRule: DstRule;
  readonly kind: AirportKind;
  readonly dataSource: AirportDataSource;
  /** Longest usable runway, e.g. 4423. */
  readonly runwayLengthMeters: number;
  /** Max aircraft movements per hour, index = UTC hour (e.g. 60). */
  readonly slotCapacityHourly: HourlyProfile;
  readonly catchmentPopulation: number;
  readonly gdpPerCapitaUSD: number;
  readonly landingFeePerTonUSD: number;
  readonly hasMaintenanceBase: boolean;
}

/* ========================================================================== *
 * 4. AIRCRAFT TYPES
 * ========================================================================== */

export type AircraftCategory =
  | 'TURBOPROP_REGIONAL'
  | 'REGIONAL_JET'
  | 'NARROWBODY'
  | 'WIDEBODY'
  | 'SUPERJUMBO'
  | 'SUPERSONIC';

export type EngineType = 'PISTON' | 'TURBOPROP' | 'TURBOFAN' | 'TURBOJET';

/** Maintenance cycle definition (SPEC 1 §3). */
export interface MaintenanceProfile {
  /** A-Check interval in flight hours (default 500). */
  readonly aCheckIntervalFlightHours: number;
  /** A-Check downtime in hours (default 12). */
  readonly aCheckDowntimeHours: number;
  /** C-Check interval in flight hours (default 6,000). */
  readonly cCheckIntervalFlightHours: number;
  /** C-Check downtime in days (default 7); needs a heavy maintenance base. */
  readonly cCheckDowntimeDays: number;
  /** D-Check interval in years (default 6). */
  readonly dCheckIntervalYears: number;
  /** D-Check downtime in days (default 21). */
  readonly dCheckDowntimeDays: number;
  /** Variable maintenance cost accrued per flight hour. */
  readonly maintenanceCostPerFlightHourUSD: number;
  /** Cost of each check type when performed. */
  readonly aCheckCostUSD: number;
  readonly cCheckCostUSD: number;
  readonly dCheckCostUSD: number;
}

export interface AircraftCrewRequirements {
  readonly cockpitCrew: number;
  /** One cabin crew member per this many installed seats (regulatory ratio). */
  readonly seatsPerCabinCrew: number;
  readonly minCabinCrew: number;
}

export interface AircraftType {
  /** e.g. "A320neo". */
  readonly id: AircraftTypeId;
  /** Airframe family for commonality, e.g. "A320". */
  readonly familyId: AircraftFamilyId;
  /** Engine family for commonality, e.g. "CFM-LEAP". */
  readonly engineFamilyId: EngineFamilyId;
  /** Catalogue name from aircraft.md, e.g. "Airbus A320neo". */
  readonly name: string;
  readonly manufacturer: string;
  /** IATA equipment code from aircraft.md; null where the file has \N. */
  readonly iataCode: string | null;
  /** ICAO type designator from aircraft.md; null where the file has \N. */
  readonly icaoCode: string | null;
  readonly category: AircraftCategory;
  readonly engineType: EngineType;
  readonly engineCount: number;
  /** Minimum runway for operations (A380 ≥ 3,000 m). */
  readonly minRunwayRequiredMeters: number;
  readonly maxRangeKm: number;
  readonly cruiseSpeedKmh: number;
  /** Fuel burn of the reference cabin, kg per flight hour. */
  readonly baseFuelBurnKgHr: number;
  /** Total usable cabin floor space in units (A320 = 180, B777 = 400). */
  readonly cabinFloorUnits: number;
  /** Regulatory ceiling on installed seats regardless of floor budget. */
  readonly maxCertifiedSeats: number;
  readonly maxTakeoffWeightKg: number;
  readonly operatingEmptyWeightKg: number;
  readonly maxPayloadKg: number;
  readonly fuelCapacityKg: number;
  /** Minimum turnaround (30 min regional … 90 min widebody). */
  readonly minTurnaroundMinutes: number;
  readonly purchaseCostUSD: number;
  readonly monthlyLeaseUSD: number;
  /** Finance-lease monthly payment (higher than operating lease; ownership transfers). */
  readonly monthlyFinanceLeaseUSD: number;
  readonly maintenance: MaintenanceProfile;
  readonly crew: AircraftCrewRequirements;
  readonly introducedYear: number;
  /** Year the type stops being available for new acquisition; null if still in production. */
  readonly productionEndYear: number | null;
}

/* ========================================================================== *
 * 5. CABIN CONFIGURATION
 * ========================================================================== */

export interface CabinConfiguration {
  readonly economySeats: number;
  /** 28–34 inches. */
  readonly economyPitchInches: number;
  readonly premiumEconomySeats: number;
  readonly businessSeats: number;
  /** 60–78 inches (lie-flat). */
  readonly businessPitchInches: number;
  readonly firstSeats: number;
  /** Number of galley modules installed (weight + fuel burn, catering quality). */
  readonly galleyCount: number;
  readonly wifiEquipped: boolean;
  readonly inSeatPower: boolean;
}

/** Share of installed seats per class; the four values sum to 1 (or all 0). */
export type ClassRatios = ClassMap<number>;

export type CabinIssueSeverity = 'ERROR' | 'WARNING' | 'INFO';

export type CabinIssueCode =
  | 'INVALID_NUMBER'
  | 'NEGATIVE_SEAT_COUNT'
  | 'NON_INTEGER_SEAT_COUNT'
  | 'INVALID_GALLEY_COUNT'
  | 'NO_SEATS_INSTALLED'
  | 'FLOOR_BUDGET_EXCEEDED'
  | 'FLOOR_BUDGET_NEARLY_FULL'
  | 'FLOOR_BUDGET_UNDERUSED'
  | 'CERTIFIED_SEATS_EXCEEDED'
  | 'CERTIFIED_SEATS_NEAR_LIMIT'
  | 'PITCH_OUT_OF_RANGE'
  | 'TIGHT_ECONOMY_PITCH'
  | 'INSUFFICIENT_GALLEYS'
  | 'EXCESS_GALLEYS'
  | 'CABIN_CREW_REQUIREMENT';

export interface CabinValidationIssue {
  readonly code: CabinIssueCode;
  readonly severity: CabinIssueSeverity;
  readonly message: string;
  /** The configuration field the issue relates to; null when it concerns the whole cabin. */
  readonly field: keyof CabinConfiguration | null;
}

/** Outcome of validating a CabinConfiguration against an AircraftType. */
export interface CabinValidationResult {
  readonly aircraftTypeId: AircraftTypeId;
  /** True when there are no ERROR-level issues. Warnings and info do not block. */
  readonly isValid: boolean;
  /** Ordered ERROR, then WARNING, then INFO. */
  readonly issues: readonly CabinValidationIssue[];
  readonly errorCount: number;
  readonly warningCount: number;
  readonly infoCount: number;
  readonly floorUnitsUsed: number;
  readonly floorUnitsBudget: number;
  readonly totalSeats: number;
  /** Maximum passengers certified for the type (exit and door limit). */
  readonly certifiedSeatLimit: number;
  readonly requiredCabinCrew: number;
  readonly recommendedGalleys: number;
}

/** Added cabin weight split by source, in kilograms. */
export interface CabinWeightBreakdown {
  readonly seatsKg: number;
  readonly galleysKg: number;
  readonly wifiKg: number;
  readonly powerKg: number;
  readonly totalKg: number;
}

/** Derived numbers for a CabinConfiguration on a specific AircraftType. */
export interface CabinMetrics {
  readonly aircraftTypeId: AircraftTypeId;
  readonly floorUnitsBudget: number;
  readonly floorUnitsUsed: number;
  readonly floorUnitsRemaining: number;
  readonly totalSeats: number;
  readonly seatsByClass: ClassMap<number>;
  readonly classRatios: ClassRatios;
  /** Floor units consumed by each class (pitch-scaled for Economy and Business). */
  readonly floorUnitsByClass: ClassMap<number>;
  /** Total weight of seats, galleys, Wi-Fi hardware and power systems. */
  readonly cabinWeightKg: number;
  readonly weightBreakdown: CabinWeightBreakdown;
  /** Cabin weight minus the type's reference cabin weight (negative = lighter). */
  readonly weightDeltaKg: number;
  /** Resulting burn after cabin weight: kg per flight hour. */
  readonly effectiveFuelBurnKgHr: number;
  /** effectiveFuelBurnKgHr minus the type's base burn. */
  readonly fuelBurnDeltaKgHr: number;
  /** 0–100 per class; 0 for a class with no seats. */
  readonly comfortByClass: ClassMap<number>;
  /** Seat-weighted average of the class comfort scores, 0–100. */
  readonly comfortScore: number;
  /** Per-class multiplier on passenger price tolerance (1.0 = neutral). */
  readonly priceToleranceByClass: ClassMap<number>;
  /** Seat-weighted average of the class multipliers (1.0 = neutral). */
  readonly priceToleranceMultiplier: number;
  /** Cabin crew needed to dispatch with this seat count. */
  readonly requiredCabinCrew: number;
  readonly isValid: boolean;
  readonly issues: readonly CabinValidationIssue[];
}

/* ========================================================================== *
 * 6. AIRCRAFT INSTANCES (OWNED / LEASED FLEET)
 * ========================================================================== */

export type AcquisitionType = 'PURCHASE' | 'OPERATING_LEASE' | 'FINANCE_LEASE';

export interface PurchaseTerms {
  readonly type: 'PURCHASE';
  readonly acquiredAt: SimMinutes;
  readonly purchasePriceUSD: UsdAmount;
}

export interface OperatingLeaseTerms {
  readonly type: 'OPERATING_LEASE';
  readonly acquiredAt: SimMinutes;
  readonly monthlyPaymentUSD: UsdAmount;
  readonly termMonths: number;
  readonly leaseEndsAt: SimMinutes;
  /** Lessor requires a completed D-Check before return. */
  readonly dCheckRequiredOnReturn: boolean;
}

export interface FinanceLeaseTerms {
  readonly type: 'FINANCE_LEASE';
  readonly acquiredAt: SimMinutes;
  readonly monthlyPaymentUSD: UsdAmount;
  readonly termMonths: number;
  readonly leaseEndsAt: SimMinutes;
  readonly residualValueUSD: UsdAmount;
  readonly outstandingPrincipalUSD: UsdAmount;
  /** True once the term completes and title passes to the airline. */
  readonly ownershipTransfersAtEnd: boolean;
}

export type AcquisitionTerms =
  | PurchaseTerms
  | OperatingLeaseTerms
  | FinanceLeaseTerms;

export type CheckType = 'A' | 'C' | 'D';

export type AirworthinessState =
  | 'SERVICEABLE'
  | 'CHECK_DUE'
  | 'IN_CHECK'
  | 'AOG';

export interface ActiveMaintenanceCheck {
  readonly check: CheckType;
  readonly startedAt: SimMinutes;
  readonly completesAt: SimMinutes;
  /** Where the work is carried out (must have a maintenance base for C/D). */
  readonly baseAirportId: AirportId;
  readonly costUSD: UsdAmount;
}

export interface MaintenanceRecord {
  readonly check: CheckType;
  readonly startedAt: SimMinutes;
  readonly completedAt: SimMinutes;
  readonly baseAirportId: AirportId;
  readonly costUSD: UsdAmount;
  readonly flightHoursAtCompletion: number;
}

export interface MaintenanceStatus {
  readonly airworthiness: AirworthinessState;
  readonly flightHoursSinceACheck: number;
  readonly flightHoursSinceCCheck: number;
  /** Completion time of last D-Check; null if never performed (delivery date applies). */
  readonly lastDCheckCompletedAt: SimMinutes | null;
  readonly nextDCheckDueAt: SimMinutes;
  /** Airframe condition, 0 (unairworthy) – 100 (new). */
  readonly conditionPct: number;
  readonly activeCheck: ActiveMaintenanceCheck | null;
  readonly history: readonly MaintenanceRecord[];
}

export type AircraftLocation =
  | { readonly kind: 'AT_AIRPORT'; readonly airportId: AirportId }
  | { readonly kind: 'IN_FLIGHT'; readonly flightOperationId: FlightOperationId };

export type AircraftOperationalState =
  | 'IDLE'
  | 'BOARDING'
  | 'IN_FLIGHT'
  | 'TURNAROUND'
  | 'MAINTENANCE'
  | 'GROUNDED';

export interface AircraftInstance {
  readonly registration: Registration;
  readonly typeId: AircraftTypeId;
  readonly familyId: AircraftFamilyId;
  readonly acquisition: AcquisitionTerms;
  readonly deliveredAt: SimMinutes;
  readonly manufactureYear: number;
  readonly totalFlightHours: number;
  readonly totalCycles: number;
  readonly cabin: CabinConfiguration;
  readonly maintenance: MaintenanceStatus;
  readonly location: AircraftLocation;
  readonly state: AircraftOperationalState;
  /** Rotation currently flown by this aircraft; null if unassigned. */
  readonly assignedScheduleId: ScheduleId | null;
  /** Home base for turnarounds / overnight parking. */
  readonly baseAirportId: AirportId;
}

/** Fleet commonality discounts for one airframe/engine family (max −15 %). */
export interface FleetCommonalityBonus {
  readonly familyId: AircraftFamilyId;
  readonly engineFamilyId: EngineFamilyId;
  readonly aircraftCount: number;
  /** 0–15. */
  readonly maintenanceDiscountPct: number;
  /** 0–15. */
  readonly crewTrainingDiscountPct: number;
}

/* ========================================================================== *
 * 7. FLIGHT LEGS & SCHEDULES
 * ========================================================================== */

/** A single fare bucket (booking class) inside a cabin class. */
export interface FareBucket {
  /** Booking code, e.g. "Y", "M", "B", "H". */
  readonly code: string;
  readonly fareUSD: UsdAmount;
  /** Max seats sellable in this bucket on one flight. */
  readonly seatLimit: number;
}

export interface ClassFare {
  readonly seatClass: SeatClass;
  /** Headline fare; the api_contracts `fareEconomy` / `fareBusiness` fields map here. */
  readonly baseFareUSD: UsdAmount;
  /** Ordered cheapest → most expensive. Empty until Yield Management unlocks buckets. */
  readonly buckets: readonly FareBucket[];
  /** When true the Yield Management dept adjusts buckets automatically. */
  readonly autoPricing: boolean;
}

export type FareStructure = ClassMap<ClassFare>;

export type LegStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED';

/**
 * A recurring weekly flight: same origin, destination and departure time on
 * each operating day. Referenced by `CREATE_FLIGHT_LEG` in api_contracts.
 */
export interface FlightLeg {
  readonly id: LegId;
  /** e.g. "FL102". */
  readonly flightNumber: string;
  readonly originId: AirportId;
  readonly destinationId: AirportId;
  readonly aircraftReg: Registration;
  readonly scheduleId: ScheduleId | null;
  readonly operatingDays: readonly DayOfWeek[];
  /** Departure minute of day, UTC (0–1439). */
  readonly departureMinuteOfDay: number;
  /** Gate-to-gate duration in minutes. */
  readonly blockMinutes: number;
  /** Whole days between departure and arrival (0 or 1 for typical legs). */
  readonly arrivalDayOffset: number;
  /** Arrival minute of day, UTC (0–1439). */
  readonly arrivalMinuteOfDay: number;
  readonly distanceKm: number;
  readonly pricing: FareStructure;
  readonly status: LegStatus;
}

export type RotationStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED';

/** One step of a rotation: a leg plus the ground time that follows it. */
export interface RotationStep {
  readonly legId: LegId;
  /** Ground time after arrival before the next step; must meet the type minimum. */
  readonly turnaroundMinutes: number;
}

export type ScheduleIssueCode =
  | 'TURNAROUND_TOO_SHORT'
  | 'DISCONTINUOUS_ROUTE'
  | 'RANGE_EXCEEDED'
  | 'RUNWAY_TOO_SHORT'
  | 'SLOT_UNAVAILABLE'
  | 'AIRCRAFT_OVERLAP'
  | 'MAINTENANCE_CONFLICT';

export interface ScheduleValidationIssue {
  readonly code: ScheduleIssueCode;
  readonly message: string;
  readonly legId: LegId | null;
}

/**
 * Multi-leg aircraft rotation, e.g. JFK → LHR → CDG → JFK.
 * A closed loop returns the aircraft to the first origin.
 */
export interface FlightSchedule {
  readonly id: ScheduleId;
  readonly name: string;
  readonly aircraftReg: Registration;
  readonly steps: readonly RotationStep[];
  readonly isClosedLoop: boolean;
  /** Total minutes from first departure until the loop can repeat. */
  readonly cycleMinutes: number;
  readonly status: RotationStatus;
  readonly issues: readonly ScheduleValidationIssue[];
  readonly createdAt: SimMinutes;
}

export type FlightOperationStatus =
  | 'SCHEDULED'
  | 'BOARDING'
  | 'ENROUTE'
  | 'ARRIVED'
  | 'CANCELLED'
  | 'DIVERTED';

export type CancellationReason =
  | 'CREW_SHORTAGE'
  | 'LABOUR_ACTION'
  | 'MAINTENANCE'
  | 'SLOT_LOSS'
  | 'WEATHER'
  | 'NO_AIRCRAFT';

/** One dated operation of a FlightLeg (the runtime instance of the weekly pattern). */
export interface FlightOperation {
  readonly id: FlightOperationId;
  readonly legId: LegId;
  readonly aircraftReg: Registration;
  readonly originId: AirportId;
  readonly destinationId: AirportId;
  readonly scheduledDepartureAt: SimMinutes;
  readonly scheduledArrivalAt: SimMinutes;
  readonly actualDepartureAt: SimMinutes | null;
  readonly actualArrivalAt: SimMinutes | null;
  readonly status: FlightOperationStatus;
  readonly cancellationReason: CancellationReason | null;
  readonly seatsOffered: ClassMap<number>;
  readonly passengersBooked: ClassMap<number>;
  readonly revenueUSD: UsdAmount;
  readonly fuelBurnedKg: number;
  readonly fuelCostUSD: UsdAmount;
}

/* ========================================================================== *
 * 8. COMPANY HQ, HUBS & DEPARTMENTS
 * ========================================================================== */

/** 0 = not yet established; 1–5 = department levels from SPEC 1. */
export type DepartmentLevel = 0 | 1 | 2 | 3 | 4 | 5;

export type HedgeTermMonths = 3 | 6 | 12;

export interface FuelHedgeContract {
  readonly id: HedgeContractId;
  readonly pricePerGallonUSD: number;
  readonly barrelsPerMonth: number;
  readonly barrelsRemaining: number;
  readonly termMonths: HedgeTermMonths;
  readonly startedAt: SimMinutes;
  readonly expiresAt: SimMinutes;
}

export interface MarketingCampaign {
  readonly id: CampaignId;
  readonly countryCode: CountryCode;
  readonly monthlyBudgetUSD: UsdAmount;
  readonly startedAt: SimMinutes;
  readonly endsAt: SimMinutes;
  /** Brand awareness points gained per month while active. */
  readonly awarenessGainPerMonth: number;
}

export type UnionRelationsStatus =
  | 'COOPERATIVE'
  | 'STABLE'
  | 'STRAINED'
  | 'DISPUTE'
  | 'STRIKE';

/** HR & Pilot Union Relations department state. */
export interface HRState {
  /** Salary slider relative to market: 1.0 = market rate. */
  readonly salaryIndex: number;
  /** 0–100. */
  readonly employeeMoralePct: number;
  /** Daily probability (0–100) that a flight is cancelled due to labour issues. */
  readonly cancellationRiskPct: number;
  readonly unionStatus: UnionRelationsStatus;
  readonly pilots: number;
  readonly cabinCrew: number;
  readonly groundStaff: number;
}

export interface DepartmentLevels {
  /** 1–5 */
  readonly marketing: DepartmentLevel;
  /** 1–5; unlocks dynamic auto-pricing and seat bucket automation. */
  readonly yieldManagement: DepartmentLevel;
  /** 1–5 */
  readonly maintenanceTech: DepartmentLevel;
  /** 1–5; unlocks fuel hedging terms and volume. */
  readonly fuelHedgingDesk: DepartmentLevel;
  /** 1–5; widens the salary negotiation range. */
  readonly hrUnionRelations: DepartmentLevel;
}

export interface SlotHolding {
  readonly airportId: AirportId;
  /** Hour of day (UTC) the slots belong to. */
  readonly hour: HourOfDay;
  /** Slots per hour held by the airline. */
  readonly slotsHeld: number;
  /** Slots per hour currently used by scheduled operations. */
  readonly slotsUsed: number;
  readonly annualLicenseCostUSD: UsdAmount;
  readonly acquiredAt: SimMinutes;
}

export type ConnectionBankType = 'ARRIVAL' | 'DEPARTURE';

/** A timed wave of arrivals or departures that allows efficient connections. */
export interface ConnectionBank {
  readonly type: ConnectionBankType;
  readonly startMinuteOfDay: number;
  readonly durationMinutes: number;
}

export interface HubState {
  readonly airportId: AirportId;
  readonly openedAt: SimMinutes;
  readonly isMainHQ: boolean;
  readonly connectionBanks: readonly ConnectionBank[];
  readonly slotHoldings: readonly SlotHolding[];
  readonly licenseFeePaidUSD: UsdAmount;
  readonly monthlyOperatingCostUSD: UsdAmount;
  /** Connecting passengers per day currently being handled. */
  readonly connectingPassengersPerDay: number;
  /** Share of hub passengers that connect (0–1). */
  readonly connectionRatio: number;
}

export interface CompanyHQ {
  readonly headquartersAirportId: AirportId;
  /** Airport IDs designated as hubs (HQ included). */
  readonly hubs: readonly AirportId[];
  /** Hub detail, keyed by AirportId; keys equal `hubs`. */
  readonly hubStates: Readonly<Record<AirportId, HubState>>;
  readonly fuelHedges: readonly FuelHedgeContract[];
  readonly departmentLevels: DepartmentLevels;
  readonly marketingCampaigns: readonly MarketingCampaign[];
  readonly hr: HRState;
  /** Airports with a heavy maintenance base (required for C/D-Checks). */
  readonly maintenanceBaseIds: readonly AirportId[];
  /** Country code → reputation score 0–100. */
  readonly reputationByRegion: Readonly<Record<CountryCode, number>>;
  /** Country code → brand awareness 0–100. */
  readonly brandAwarenessByRegion: Readonly<Record<CountryCode, number>>;
}

/* ========================================================================== *
 * 9. PASSENGER DEMAND
 * ========================================================================== */

/** Multipliers applied to base demand at a given moment. */
export interface DemandModifiers {
  /** By month of year. */
  readonly seasonalityMultiplier: number;
  /** By day of week (business vs leisure shape). */
  readonly dayOfWeekMultiplier: number;
  /** By departure hour. */
  readonly hourlyMultiplier: number;
}

/** How a city's travel market skews, from its airport and country. */
export type CityProfileKind = 'BUSINESS_HUB' | 'LEISURE_DESTINATION' | 'BALANCED';

/**
 * Time-independent description of one directional market, produced by the
 * economic gravity model. `baseDailyDemand` is the annual-average day; the
 * temporal engine layers season, weekday and hour on top of it.
 */
export interface MarketProfile {
  readonly od: ODPair;
  readonly distanceKm: number;
  readonly gravityConstant: number;
  /** K·(PopA·GDPA)(PopB·GDPB)/Distance^1.5 before shaping, passengers/day. */
  readonly rawGravityDemand: number;
  /** 0–1 factor applied for ground-transport substitution and ultra-long-haul decay. */
  readonly distanceAttenuation: number;
  /** Passengers per day on an average day, after attenuation and saturation. */
  readonly baseDailyDemand: number;
  /** Fraction of demand that is business travel (0–1). */
  readonly businessShare: number;
  /** 1 − businessShare. */
  readonly leisureShare: number;
  readonly businessDailyDemand: number;
  readonly leisureDailyDemand: number;
  readonly originProfile: CityProfileKind;
  readonly destinationProfile: CityProfileKind;
  /** Mean latitude of the two airports; sets the leisure season. */
  readonly meanLatitude: number;
  /** Average GDP per capita of the two ends (geometric mean), USD. */
  readonly meanGdpPerCapitaUSD: number;
}

/** Passengers wanting to depart in one origin-local hour, split by purpose. */
export interface HourlyDemand {
  readonly od: ODPair;
  readonly month: MonthOfYear;
  readonly dayOfWeek: DayOfWeek;
  /** Origin-local hour of departure. */
  readonly hour: HourOfDay;
  readonly businessPassengers: number;
  readonly leisurePassengers: number;
  readonly totalPassengers: number;
  readonly businessModifiers: DemandModifiers;
  readonly leisureModifiers: DemandModifiers;
}

/**
 * Directional O&D demand using the gravity model:
 *   base = K * ((Pop_A * GDP_A) * (Pop_B * GDP_B)) / Distance^1.5
 */
export interface PassengerDemand {
  readonly od: ODPair;
  readonly distanceKm: number;
  /** Gravity constant K used for this matrix. */
  readonly gravityConstant: number;
  /** Daily base demand before modifiers (passengers/day). */
  readonly baseDailyDemand: number;
  /** Fraction of demand that is business travel (0–1). */
  readonly businessShare: number;
  readonly modifiers: DemandModifiers;
  /** `baseDailyDemand` after all modifiers (passengers/day). */
  readonly effectiveDailyDemand: number;
  readonly demandByClass: ClassMap<number>;
  /** Share of daily demand wanting to depart in each UTC hour; sums to 1. */
  readonly hourlyDistribution: HourlyProfile;
  /** Price elasticity of demand (negative number, e.g. −1.2). */
  readonly priceElasticity: number;
  /** Reference fare per class at which demand above applies. */
  readonly referenceFareUSD: ClassMap<number>;
}

/** Demand for every O&D pair, keyed by "ORG-DST". */
export interface DemandMatrix {
  readonly generatedAt: SimMinutes;
  readonly month: MonthOfYear;
  readonly entries: Readonly<Record<RouteKey, PassengerDemand>>;
}

/* ========================================================================== *
 * 10. FINANCIAL LEDGER
 * ========================================================================== */

export interface RevenueBreakdown {
  readonly ticketsUSD: ClassMap<UsdAmount>;
  readonly ancillaryUSD: UsdAmount;
  readonly cargoUSD: UsdAmount;
  readonly totalUSD: UsdAmount;
}

export interface ExpenseBreakdown {
  readonly fuelUSD: UsdAmount;
  /** Savings (+) or losses (−) from hedge contracts versus spot. */
  readonly hedgeSettlementUSD: UsdAmount;
  readonly crewUSD: UsdAmount;
  readonly maintenanceUSD: UsdAmount;
  readonly landingFeesUSD: UsdAmount;
  readonly leasePaymentsUSD: UsdAmount;
  readonly aircraftFinancingInterestUSD: UsdAmount;
  readonly depreciationUSD: UsdAmount;
  readonly slotAndHubCostsUSD: UsdAmount;
  readonly marketingUSD: UsdAmount;
  readonly departmentUSD: UsdAmount;
  readonly overheadUSD: UsdAmount;
  readonly totalUSD: UsdAmount;
}

export type LedgerCategory =
  | 'TICKET_REVENUE'
  | 'ANCILLARY_REVENUE'
  | 'CARGO_REVENUE'
  | 'FUEL'
  | 'HEDGE_SETTLEMENT'
  | 'CREW'
  | 'MAINTENANCE'
  | 'LANDING_FEES'
  | 'LEASE_PAYMENT'
  | 'FINANCING_INTEREST'
  | 'AIRCRAFT_PURCHASE'
  | 'AIRCRAFT_SALE'
  | 'SLOT_AND_HUB'
  | 'MARKETING'
  | 'DEPARTMENT'
  | 'OVERHEAD'
  | 'LOAN_PROCEEDS'
  | 'LOAN_REPAYMENT';

/** A single signed cash movement. Positive = money in, negative = money out. */
export interface LedgerEntry {
  readonly id: string;
  readonly day: SimDay;
  readonly category: LedgerCategory;
  readonly amountUSD: UsdAmount;
  readonly description: string;
  readonly legId: LegId | null;
  readonly aircraftReg: Registration | null;
  readonly airportId: AirportId | null;
}

/** End-of-day snapshot, sent to the UI as part of `TICK_DAILY`. */
export interface FinancialLedger {
  readonly day: SimDay;
  readonly cashBalanceUSD: UsdAmount;
  readonly debtUSD: UsdAmount;
  readonly revenue: RevenueBreakdown;
  readonly expenses: ExpenseBreakdown;
  readonly operatingProfitUSD: UsdAmount;
  readonly netIncomeUSD: UsdAmount;
  readonly cumulativeNetIncomeUSD: UsdAmount;
  /** Entries posted during this day. */
  readonly entries: readonly LedgerEntry[];
}

/* ========================================================================== *
 * 11. ROUTE ANALYTICS
 * ========================================================================== */

/** Cost and profit for a route over the analysed period. */
export interface RouteFinancials {
  readonly revenueUSD: UsdAmount;
  readonly fuelCostUSD: UsdAmount;
  readonly crewCostUSD: UsdAmount;
  readonly maintenanceCostUSD: UsdAmount;
  readonly landingFeesUSD: UsdAmount;
  readonly ownershipCostUSD: UsdAmount;
  readonly totalCostUSD: UsdAmount;
  readonly profitUSD: UsdAmount;
  readonly marginPct: number;
}

/**
 * Spill and leakage, per class:
 *   spilled   = demand that wanted this route but was turned away by capacity
 *   recaptured = spilled demand that rebooked on another of our flights
 *   leaked    = demand lost to competitors or to our fares being too high
 */
export interface SpillLeakageMetrics {
  readonly spilled: ClassMap<number>;
  readonly recaptured: ClassMap<number>;
  readonly leakedToCompetitors: ClassMap<number>;
  readonly leakedToPrice: ClassMap<number>;
}

/** Per-route, per-day analytics. Sent in `TICK_DAILY`. */
export interface RouteAnalytics {
  readonly routeKey: RouteKey;
  readonly od: ODPair;
  readonly day: SimDay;
  readonly legIds: readonly LegId[];
  readonly distanceKm: number;
  readonly flightsOperated: number;
  readonly flightsCancelled: number;
  readonly seatsOffered: ClassMap<number>;
  readonly passengersCarried: ClassMap<number>;
  readonly totalPassengers: number;
  /** Per-class load factors, 0–100. */
  readonly loadFactorPct: ClassMap<number>;
  readonly overallLoadFactorPct: number;
  /** Gross demand for the route on this day (passengers). */
  readonly demandRequested: number;
  readonly spillLeakage: SpillLeakageMetrics;
  /** Passengers whose full journey is this route. */
  readonly localPassengers: number;
  /** Passengers connecting onto or from other legs at a hub. */
  readonly connectingPassengers: number;
  readonly averageFareUSD: ClassMap<number>;
  /** Available seat kilometres. */
  readonly askKm: number;
  /** Revenue passenger kilometres. */
  readonly rpkKm: number;
  /** Revenue per RPK. */
  readonly yieldPerRpkUSD: number;
  /** Revenue per ASK. */
  readonly raskUSD: number;
  /** Cost per ASK. */
  readonly caskUSD: number;
  /** Our share of total market demand on the OD pair, 0–100. */
  readonly marketSharePct: number;
  readonly financials: RouteFinancials;
}
